import SpotifyIcon from "../../assets/Spotify_Icon_RGB_Green.png";
import AppleMusicIcon from "../../assets/appleMusic.svg";
import YouTubeMusicIcon from "../../assets/youtube-music-icon.png";
import YouTubeIcon from "../../assets/youtube_social_icon_red.png";
import TidalIcon from "../../assets/tidal_logo_black.png";
import { Link } from "react-router-dom";

/** One directed pair: transfer + optional sync deep links */
export interface StreamPairLink {
    toIcon: string;
    text: string;
    link: string;
    fromIcon: string;
    syncLink?: string;
    syncText?: string;
}

interface Service {
    /** Outgoing from this hub service → other services */
    from: StreamPairLink[];
    /** Incoming from other services → this hub service */
    to: StreamPairLink[];
}

function pair(
    fromIcon: string,
    toIcon: string,
    fromSlug: string,
    toSlug: string,
    fromLabel: string,
    toLabel: string
): StreamPairLink {
    const base = `${fromSlug}-to-${toSlug}`;
    return {
        fromIcon,
        toIcon,
        text: `Transfer from ${fromLabel} to ${toLabel}`,
        link: `/transfer/${base}`,
        syncLink: `/sync/${base}`,
        syncText: `Sync from ${fromLabel} to ${toLabel}`,
    };
}

const H = {
    spotify: { icon: SpotifyIcon, slug: "spotify", label: "Spotify" },
    appleMusic: { icon: AppleMusicIcon, slug: "apple-music", label: "Apple Music" },
    youtubeMusic: { icon: YouTubeMusicIcon, slug: "youtube-music", label: "YouTube Music" },
    youtube: { icon: YouTubeIcon, slug: "youtube", label: "YouTube" },
    tidal: { icon: TidalIcon, slug: "tidal", label: "Tidal" },
} as const;

type HubKey = keyof typeof H;

function hubFromTo(hub: HubKey): Service {
    const self = H[hub];
    const others = (Object.keys(H) as HubKey[]).filter((k) => k !== hub);
    return {
        from: (Object.keys(H) as HubKey[]).map((target) =>
            pair(self.icon, H[target].icon, self.slug, H[target].slug, self.label, H[target].label)
        ),
        to: others.map((source) =>
            pair(H[source].icon, self.icon, H[source].slug, self.slug, H[source].label, self.label)
        ),
    };
}

/**
 * Hub keys: spotify | appleMusic | youtubeMusic | youtube | tidal
 * - `from`: this service → others (outgoing transfers from hub, includes same-service row)
 * - `to`: others → this service (incoming transfers to hub; same-service row omitted)
 */
export const TransferLinksDataFromStreamtoStream: { [key: string]: Service } = {
    spotify: hubFromTo("spotify"),
    appleMusic: hubFromTo("appleMusic"),
    youtubeMusic: hubFromTo("youtubeMusic"),
    youtube: hubFromTo("youtube"),
    tidal: hubFromTo("tidal"),
};

export function LinksFromStreamtoStreamForTransfer({ links }: { links: StreamPairLink[] }) {
    return (
        <div>
            {links.map((item, index) => (
                <div key={index} className="divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden rounded-lg bg-white dark:bg-zinc-800 shadow-sm mt-4 mb-4">
                    <div className="px-4 py-5 sm:px-6 text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-150">
                        <div className="flex items-center space-x-4">
                            <img src={item.fromIcon} alt="" style={{ height: "26px" }} />
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <img src={item.toIcon} alt="" style={{ height: "26px" }} />
                            <Link to={item.link} className="text-lg font-semibold">
                                {item.text}
                            </Link>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function LinksFromStreamtoStreamForSync({ links }: { links: StreamPairLink[] }) {
    return (
        <div>
            {links.map((item, index) => (
                <div key={index} className="divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden rounded-lg bg-white dark:bg-zinc-800 shadow-sm mt-4 mb-4">
                    <div className="px-4 py-5 sm:px-6 text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-150">
                        <div className="flex items-center space-x-4">
                            <img src={item.fromIcon} alt="" style={{ height: "26px" }} />
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <img src={item.toIcon} alt="" style={{ height: "26px" }} />
                            <Link to={item.syncLink || ""} className="text-lg font-semibold">
                                {item.syncText}
                            </Link>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
